import React from 'react';
import { Search, Filter, Clock, Users, Star, TrendingUp } from 'lucide-react';
import { Button } from '@/components/design-system/Button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface FilterOptions {
  search: string;
  cuisine: string;
  diet: string;
  difficulty: string;
  timeRange: string;
  sort: string;
}

interface SearchAndFiltersProps {
  filters: FilterOptions;
  showFilters: boolean;
  onUpdateFilters: (newFilters: Partial<FilterOptions>) => void;
  onToggleFilters: () => void;
}

const cuisineOptions = [
  'Italian', 'Mexican', 'Asian', 'American', 'Mediterranean',
  'Indian', 'French', 'Thai', 'Chinese', 'Japanese'
];

const dietOptions = [
  'Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Keto',
  'Paleo', 'Low-Carb', 'Healthy', 'Quick & Easy'
];

export const SearchAndFilters: React.FC<SearchAndFiltersProps> = ({
  filters,
  showFilters,
  onUpdateFilters,
  onToggleFilters
}) => {
  return (
    <div className="border-b border-gray-200" style={{ backgroundColor: '#E6EFEA' }}>
      <div className="max-w-7xl mx-auto px-4 py-1">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            {/* Header with logo - matching Shopping List format */}
            <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
              <img 
                src="/dinnerbrain-kawaii-logo-iphone.png" 
                alt="DinnerBrain with iPhone" 
                className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
              />
              <h1 className="font-bold text-gray-900 type-h1">Community Recipes</h1>
            </div>
            <p className="text-gray-600 text-center md:text-left">Discover delicious recipes shared by our cooking community</p>
          </div>

          {/* Search Bar */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 md:w-80">
              <Search className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" style={{ left: '16px' }} />
              <Input
                placeholder="Search recipes or ingredients..."
                className="pl-12"
                value={filters.search}
                onChange={(e) => onUpdateFilters({ search: e.target.value })}
              />
            </div>
            <Button
              variant="secondary"
              onClick={onToggleFilters}
              className="flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filters
            </Button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <Select value={filters.sort} onValueChange={(value) => onUpdateFilters({ sort: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Latest
                    </div>
                  </SelectItem>
                  <SelectItem value="trending">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Trending
                    </div>
                  </SelectItem>
                  <SelectItem value="following">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Following
                    </div>
                  </SelectItem>
                  <SelectItem value="rating_average">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4" />
                      Highest Rated
                    </div>
                  </SelectItem>
                  <SelectItem value="save_count">Most Saved</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.cuisine} onValueChange={(value) => onUpdateFilters({ cuisine: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Cuisine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cuisines</SelectItem>
                  {cuisineOptions.map((cuisine) => (
                    <SelectItem key={cuisine} value={cuisine}>
                      {cuisine}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.diet} onValueChange={(value) => onUpdateFilters({ diet: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Diet" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Diets</SelectItem>
                  {dietOptions.map((diet) => (
                    <SelectItem key={diet} value={diet}>
                      {diet}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.difficulty} onValueChange={(value) => onUpdateFilters({ difficulty: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Difficulty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.timeRange} onValueChange={(value) => onUpdateFilters({ timeRange: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Cook Time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Time</SelectItem>
                  <SelectItem value="15">Under 15 min</SelectItem>
                  <SelectItem value="30">Under 30 min</SelectItem>
                  <SelectItem value="60">Under 1 hour</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Active Filters */}
            {(filters.search || filters.cuisine || filters.diet || filters.difficulty || filters.timeRange) && (
              <div className="flex flex-wrap gap-2 mt-4">
                {filters.search && (
                  <div className="inline-flex items-center gap-1 px-2 !py-1 !leading-none !h-auto !min-h-0 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200 transition-colors">
                    Search: &ldquo;{filters.search}&rdquo;
                    <button 
                      onClick={() => onUpdateFilters({ search: '' })} 
                      className="ml-1 text-blue-600 hover:text-blue-800 hover:bg-blue-300 rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold transition-colors"
                      aria-label="Remove search filter"
                    >
                      ×
                    </button>
                  </div>
                )}
                {filters.cuisine && (
                  <div className="inline-flex items-center gap-1 px-2 !py-1 !leading-none !h-auto !min-h-0 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200 hover:bg-green-200 transition-colors">
                    {filters.cuisine}
                    <button 
                      onClick={() => onUpdateFilters({ cuisine: '' })} 
                      className="ml-1 text-green-600 hover:text-green-800 hover:bg-green-300 rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold transition-colors"
                      aria-label="Remove cuisine filter"
                    >
                      ×
                    </button>
                  </div>
                )}
                {filters.diet && (
                  <div className="inline-flex items-center gap-1 px-2 !py-1 !leading-none !h-auto !min-h-0 rounded-full text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200 hover:bg-purple-200 transition-colors">
                    {filters.diet}
                    <button 
                      onClick={() => onUpdateFilters({ diet: '' })} 
                      className="ml-1 text-purple-600 hover:text-purple-800 hover:bg-purple-300 rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold transition-colors"
                      aria-label="Remove diet filter"
                    >
                      ×
                    </button>
                  </div>
                )}
                {filters.difficulty && (
                  <div className="inline-flex items-center gap-1 px-2 !py-1 !leading-none !h-auto !min-h-0 rounded-full text-sm font-medium bg-orange-100 text-orange-800 border border-orange-200 hover:bg-orange-200 transition-colors">
                    {filters.difficulty}
                    <button 
                      onClick={() => onUpdateFilters({ difficulty: '' })} 
                      className="ml-1 text-orange-600 hover:text-orange-800 hover:bg-orange-300 rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold transition-colors"
                      aria-label="Remove difficulty filter"
                    >
                      ×
                    </button>
                  </div>
                )}
                {filters.timeRange && (
                  <div className="inline-flex items-center gap-1 px-2 !py-1 !leading-none !h-auto !min-h-0 rounded-full text-sm font-medium bg-pink-100 text-pink-800 border border-pink-200 hover:bg-pink-200 transition-colors">
                    Under {filters.timeRange} min
                    <button 
                      onClick={() => onUpdateFilters({ timeRange: '' })} 
                      className="ml-1 text-pink-600 hover:text-pink-800 hover:bg-pink-300 rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold transition-colors"
                      aria-label="Remove time filter"
                    >
                      ×
                    </button>
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onUpdateFilters({ search: '', cuisine: '', diet: '', difficulty: '', timeRange: '' })}
                  className="text-gray-500 hover:text-gray-700 px-2 !py-1 !leading-none !h-auto !min-h-0 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors"
                >
                  Clear all
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};